import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onBeforeMount, provide } from 'vue';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { isBFFError } from '@/modules/common/helpers/bff';

import { LoaderIndicator } from '@warrenbrasil/nebraska-web';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  getMarginAllocation,
  getMarginAllocationPreview,
  updateLimits,
  updateTraderShield,
  updatePreferences,
  IMarginAllocation,
  IMarginAllocationPreview
} from '@/modules/trade/services/margin-allocation';

import { MarginAllocationComponents, EMarginAllocationSteps } from './helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'MarginAllocation',
  setup(__props) {

const currentStep = ref(EMarginAllocationSteps.DAY_TRADE_LIMITS);
const marginAllocation = ref<IMarginAllocation | null>(null);
const marginAllocationPreview = ref<IMarginAllocationPreview | null>(null);

const dataForm = ref({
  isUpdatingTraderShield: false,
  isUpdatingPreferences: false,
  isUpdatingLimits: false,
  balance: { value: 0, formatted: '' },
  drawdown: { value: 0, formatted: '' }
});

const isFetchingInitialData = ref(true);

const step = computed(() => {
  return {
    component: MarginAllocationComponents[currentStep.value]
  };
});

function changeStep(step: EMarginAllocationSteps) {
  currentStep.value = step;
}

function updateDataForm(
  key: string,
  value: object | string | boolean | number
) {
  dataForm.value = {
    ...dataForm.value,
    [key]: value
  };
}

function updateBalanceDrawdown() {
  dataForm.value.balance = marginAllocation.value!.balance;
  dataForm.value.drawdown = marginAllocation.value!.maximumDrawdown;
}

async function fetchInitialData() {
  try {
    isFetchingInitialData.value = true;

    marginAllocation.value = await getMarginAllocation();
    marginAllocationPreview.value = await getMarginAllocationPreview(
      marginAllocation.value.accountId
    );

    updateBalanceDrawdown();

    currentStep.value = marginAllocation.value.isFirstTime
      ? EMarginAllocationSteps.BALANCE
      : EMarginAllocationSteps.DAY_TRADE_LIMITS;
  } catch {
    currentStep.value = EMarginAllocationSteps.ERROR;
  } finally {
    isFetchingInitialData.value = false;
  }
}

async function fetchUpdateLimits() {
  try {
    dataForm.value.isUpdatingLimits = true;

    marginAllocation.value = await updateLimits({
      marginCapital: dataForm.value.balance.value,
      dailyLoss: dataForm.value.drawdown.value,
      account: marginAllocation.value!.accountId
    });

    updateBalanceDrawdown();

    currentStep.value = EMarginAllocationSteps.DAY_TRADE_LIMITS;

    notify({
      group: 'wrn-notifications',
      text: 'Seus limites foram salvos.',
      type: NotificationTypes.success
    });
  } catch (e) {
    let message =
      'Houve um problema ao salvar seus limites. Tente novamente em breve.';
    if (isBFFError(e)) {
      message = e.error.message.formatted;
    }
    notify({
      group: 'wrn-notifications',
      text: message,
      type: NotificationTypes.error
    });
  } finally {
    dataForm.value.isUpdatingLimits = false;
  }
}

async function handleTraderShield() {
  try {
    dataForm.value.isUpdatingTraderShield = true;

    const { isTraderShieldEnabled } = await updateTraderShield(
      marginAllocation.value!.accountId
    );

    marginAllocation.value = await getMarginAllocation();

    updateBalanceDrawdown();

    currentStep.value = EMarginAllocationSteps.DAY_TRADE_LIMITS;

    notify({
      group: 'wrn-notifications',
      text: isTraderShieldEnabled
        ? 'Trader Shield ativo'
        : 'Trader Shield inativo',
      type: NotificationTypes.success
    });
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao ativar/desativar o Trader Shield. Tente novamente em breve.',
      type: NotificationTypes.error
    });
  } finally {
    dataForm.value.isUpdatingTraderShield = false;
  }
}

async function fetchUpdatePreference(value: boolean) {
  try {
    dataForm.value.isUpdatingPreferences = true;

    await updatePreferences(marginAllocation.value!.accountId);

    marginAllocation.value = await getMarginAllocation();

    updateBalanceDrawdown();

    const message = value
      ? 'Suas preferências foram salvas e serão mantidas assim até a próxima edição.'
      : 'Preferências desabilitadas.';

    notify({
      group: 'wrn-notifications',
      text: message,
      type: NotificationTypes.success
    });
  } catch {
    const message = value
      ? 'Houve um problema ao salvar suas preferências. Tente novamente em breve.'
      : 'Houve um problema ao desabilitar suas preferências. Tente novamente em breve.';

    notify({
      group: 'wrn-notifications',
      text: message,
      type: NotificationTypes.error
    });
  } finally {
    dataForm.value.isUpdatingPreferences = false;
  }
}

onBeforeMount(async () => {
  await fetchInitialData();
});

provide('fetchInitialData', fetchInitialData);
provide('changeStep', changeStep);
provide('updateDataForm', updateDataForm);
provide('handleTraderShield', handleTraderShield);
provide('fetchUpdatePreference', fetchUpdatePreference);
provide('fetchUpdateLimits', fetchUpdateLimits);

provide('marginAllocation', marginAllocation);
provide('marginAllocationPreview', marginAllocationPreview);
provide('dataForm', dataForm);

return { __sfc: true,currentStep, marginAllocation, marginAllocationPreview, dataForm, isFetchingInitialData, step, changeStep, updateDataForm, updateBalanceDrawdown, fetchInitialData, fetchUpdateLimits, handleTraderShield, fetchUpdatePreference, LoaderIndicator, FadeTransition }
}

})